import { CaseTypeCategory } from './caseTypeCategory';

export interface LegalCasesData {
    lastFetchedDate?: String;
    status?: string;
    legalCasesFiledAgainstCompanyOrDirector: CaseRow[];
    legalCasesFiledByCompanyOrDirector: CaseRow[];
}

type CaseRow = {
    name: string;
    cases: Case[];
    designation?: string;
};

export type Case = {
    filedBy: string;
    filedAgainst: string;
    caseCategory: string;
    court: string;
    status: string;
    caseNum: string;
    nextHearingDate?: string;
    decisionDate?: string;
    // Not yet sent by the backend — UI must degrade gracefully when these are absent.
    standardCaseType?: string;
    // Primary confidence signal driving the Open/Disposed vs. Other Probable Cases split — see
    // isHighConfidence/hasConfidenceSignal below. matchPercentage is a legacy fallback kept for
    // payloads that still send that instead.
    confidenceLevel?: 'HIGH' | 'LOW';
    matchPercentage?: number;
    docId?: string[];
    district?: string;
    state?: string;
    // Backend-provided category for the case-type bar's colored segments/legend.
    caseTypeCategory?: CaseTypeCategory;
    // For the Case No. link — not yet sent by the backend.
    caseDetailUrl?: string;
};

// Case status values as sent by the backend.
export const STATUS = {
    PENDING: { value: 'Pending' },
    DISPOSED: { value: 'Disposed' },
    NOT_AVAILABLE: { value: 'Not Available' },
};

// Swappable confidence predicate: prefers confidenceLevel, falls back to matchPercentage, and
// defaults to HIGH so payloads with no confidence field at all keep rendering in Open/Disposed
// instead of silently dropping into Other Probable Cases.
export const CONFIDENCE_MATCH_THRESHOLD = 70;
type ConfidenceFields = { confidenceLevel?: 'HIGH' | 'LOW'; matchPercentage?: number };

export function isHighConfidence(c: ConfidenceFields): boolean {
    if (c.confidenceLevel) return c.confidenceLevel === 'HIGH';
    if (typeof c.matchPercentage === 'number') return c.matchPercentage >= CONFIDENCE_MATCH_THRESHOLD;
    return true;
}

// Whether ANY case in the dataset carries the confidenceLevel field — gates whether the "Other
// Probable Cases" bucket exists at all for this response. Responses that never send confidenceLevel
// only ever segregate into Open/Disposed (see LegalCases.tsx renderTable's visibleGroups).
export function hasConfidenceSignal(cases: ConfidenceFields[]): boolean {
    return cases.some((c) => c.confidenceLevel != null || typeof c.matchPercentage === 'number');
}

export const HEADERS = {
    FILED_BY: 'filedBy',
    FILED_AGAINST: 'filedAgainst',
    CASE_CATEGORY: 'caseCategory',
    COURT: 'court',
    STATUS: 'status',
    CASE_NUM: 'caseNum',
    REPORTS: 'docId',
    DATE: 'date',
};
const HEADER_LABELS = {
    [HEADERS.FILED_BY]: 'Filed By',
    [HEADERS.FILED_AGAINST]: 'Filed Against',
    [HEADERS.CASE_CATEGORY]: 'Case Type',
    [HEADERS.COURT]: 'Court',
    [HEADERS.STATUS]: 'Status',
    [HEADERS.CASE_NUM]: 'Case No.',
    [HEADERS.DATE]: 'Date',
    [HEADERS.REPORTS]: 'Report',
};

const HEADER_LABELS_STAR = {
    [HEADERS.FILED_BY]: 'Filed By*',
    [HEADERS.FILED_AGAINST]: 'Filed Against*',
};

export type Direction = 'filed_against' | 'filed_by';

export type Column = { key: string; label: string };

// Single source of truth for the shared table's column order — one table, one header row, both
// directions read from here. Order: Case No. -> Case Type -> Court -> [active, other Filed column] ->
// Date -> Report. The direction-active name column gets the "*" label (ties to the footer's AI-match note).
export function getColumns(direction: Direction, isPDFReport = false): Column[] {
    const [primary, secondary] =
        direction === 'filed_against' ? [HEADERS.FILED_AGAINST, HEADERS.FILED_BY] : [HEADERS.FILED_BY, HEADERS.FILED_AGAINST];

    return [
        { key: HEADERS.CASE_NUM, label: HEADER_LABELS[HEADERS.CASE_NUM] },
        { key: HEADERS.CASE_CATEGORY, label: HEADER_LABELS[HEADERS.CASE_CATEGORY] },
        { key: HEADERS.COURT, label: HEADER_LABELS[HEADERS.COURT] },
        { key: primary, label: HEADER_LABELS_STAR[primary] },
        { key: secondary, label: HEADER_LABELS[secondary] },
        { key: HEADERS.DATE, label: HEADER_LABELS[HEADERS.DATE] },
        ...(!isPDFReport ? [{ key: HEADERS.REPORTS, label: HEADER_LABELS[HEADERS.REPORTS] }] : []),
    ];
}
